import React, { Suspense, lazy, ComponentType } from 'react';
import { DataLoading } from '../design-system/components/LoadingStates';

/**
 * Unified Lazy Loading System
 * Tüm lazy loading işlemleri için tek kaynak
 */

interface LoadingFallbackProps {
  componentName?: string;
  type?: 'table' | 'list' | 'cards';
}

// Loading fallback component
const LoadingFallback: React.FC<LoadingFallbackProps> = ({ componentName = 'Component', type = 'table' }) => (
  <div className="lazy-loading-fallback">
    <DataLoading type={type} />
    <p>Loading {componentName}...</p>
  </div>
);

// Generic lazy loading wrapper with error boundary
export const withLazyLoading = <P extends object>(
  Component: () => Promise<{ default: ComponentType<P> }>,
  fallbackComponent: React.ComponentType<LoadingFallbackProps> = LoadingFallback
): React.FC<P> => {
  const LazyComponent = lazy(Component);
  
  return (props: P) => {
    const Fallback = fallbackComponent;
    return (
      <Suspense fallback={<Fallback componentName={Component.name} />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
};

// Route-based lazy loading
export const createLazyRoute = <P extends object>(
  importFunction: () => Promise<{ default: ComponentType<P> }>,
  fallback: React.ComponentType<LoadingFallbackProps> = LoadingFallback
): React.FC<P> => {
  // Wrap import function to handle errors and undefined exports
  const safeImportFunction = async (): Promise<{ default: ComponentType<P> }> => {
    try {
      const module = await importFunction();
      if (!module || !module.default) {
        // Return a fallback component
        const FallbackComponent: ComponentType<P> = ((_props: P) => (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h2>Component Yüklenemedi</h2>
            <p>Bu bileşen şu anda kullanılamıyor. Lütfen sayfayı yenileyin.</p>
            <button onClick={() => window.location.reload()}>Sayfayı Yenile</button>
          </div>
        )) as ComponentType<P>;
        return {
          default: FallbackComponent
        };
      }
      return module;
    } catch (error) {
      // Return a fallback component
      const ErrorFallbackComponent: ComponentType<P> = ((_props: P) => (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Component Yüklenemedi</h2>
          <p>Bir hata oluştu: {error instanceof Error ? error.message : 'Bilinmeyen hata'}</p>
          <button onClick={() => window.location.reload()}>Sayfayı Yenile</button>
        </div>
      )) as ComponentType<P>;
      return {
        default: ErrorFallbackComponent
      };
    }
  };
  
  const LazyComponent = lazy(safeImportFunction);
  
  return (props: P) => {
    const Fallback = fallback;
    return (
      <Suspense fallback={<Fallback />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
};

// Pre-configured lazy components for common use cases
export const LazyPage = withLazyLoading;
export const LazyModal = withLazyLoading;
export const LazyChart = withLazyLoading;

// ===== MAIN APPLICATION COMPONENTS =====

// Helper function to create fallback components
const createFallback = (componentName: string, type: 'table' | 'list' | 'cards' = 'table') => {
  return () => <LoadingFallback componentName={componentName} type={type} />;
};

// Dashboard & Main Layout
export const LazyDashboardContent = createLazyRoute(
  () => import('../DashboardContent'),
  createFallback("Dashboard", "cards")
);

export const LazySolMenu = createLazyRoute(
  () => import('../SolMenu'),
  createFallback("Sol Menu", "table")
);

export const LazyWizard = createLazyRoute(
  () => import('../Wizard'),
  createFallback("Kurulum Sihirbazı", "table")
);

// Customer Management
export const LazyCustomerList = createLazyRoute(
  () => import('../CustomerList'),
  createFallback("Müşteri Listesi", "table")
);

export const LazyNewCustomer = createLazyRoute(
  () => import('../NewCustomer'),
  createFallback("Yeni Müşteri", "table")
);

export const LazyCustomerExcelImporter = createLazyRoute(
  () => import('../CustomerExcelImporter'),
  createFallback("Excel İçe Aktarıcı", "table")
);

export const LazyTransactionsList = createLazyRoute(
  () => import(/* webpackChunkName: "transactions-list" */ '../TransactionsList'),
  createFallback("Hareketler", "table")
);

// Technical Service
export const LazyNewService = createLazyRoute(
  () => import('../NewService'),
  createFallback("Yeni Servis", "table")
);

export const LazyPendingServices = createLazyRoute(
  () => import(/* webpackChunkName: "pending-services" */ '../PendingServices'),
  createFallback("Bekleyen Servisler", "table")
);

export const LazyReadyForDelivery = createLazyRoute(
  () => import(/* webpackChunkName: "ready-for-delivery" */ '../ReadyForDelivery'),
  createFallback("Teslim Hazır Servisler", "table")
);

export const LazyDeliveredServices = createLazyRoute(
  () => import(/* webpackChunkName: "delivered-services" */ '../DeliveredServices'),
  createFallback("Teslim Edilen Servisler", "table")
);

// ServiceForm artık src/components/ServiceForm/ServiceForm.tsx'de - Route'larda direkt import ediliyor

// Personnel Management
export const LazyNewPersonel = createLazyRoute(
  () => import('../NewPersonel'),
  createFallback("Yeni Personel", "table")
);

export const LazyPersonelListesi = createLazyRoute(
  () => import('../PersonelListesi'),
  createFallback("Personel Listesi", "table")
);

// Stock Management
export const LazyNewStock = createLazyRoute(
  () => import('../NewStock'),
  createFallback("Yeni Stok", "table")
);

export const LazyStockList = createLazyRoute(
  () => import('../StockList'),
  createFallback("Stok Listesi", "table")
);

export const LazyTransferSlip = createLazyRoute(
  () => import('../TransferSlip'),
  createFallback("Transfer Fişi", "table")
);

export const LazyCreateWarehouse = createLazyRoute(
  () => import('../CreateWarehouse'),
  createFallback("Depo Oluştur", "table")
);

export const LazyStockPurchaseSlip = createLazyRoute(
  () => import('../StockPurchaseSlip'),
  createFallback("Alış Fişi", "table")
);

export const LazyStockManagerWithImport = createLazyRoute(
  () => import('../StockManagerWithImport'),
  createFallback("Stok Yöneticisi", "table")
);

// Financial Management
export const LazyDebtStatement = createLazyRoute(
  () => import('../DebtStatement'),
  createFallback("Borç Dekontu", "table")
);

export const LazyReceivableStatement = createLazyRoute(
  () => import('../ReceivableStatement'),
  createFallback("Alacak Dekontu", "table")
);

export const LazySalesInvoice = createLazyRoute(
  () => import('../SalesInvoice'),
  createFallback("Satış Faturası", "table")
);

export const LazyPurchaseInvoice = createLazyRoute(
  () => import('../PurchaseInvoice'),
  createFallback("Alış Faturası", "table")
);

export const LazySMSSender = createLazyRoute(
  () => import('../SMSSender'),
  createFallback("SMS Gönderici", "table")
);

// Reports — 13.09.2026: 12 rapor tek sayfada (src/pages/Raporlar.tsx, rota: routes/ReportRoutes.tsx).

// Settings & Administration
export const LazyProfileSettings = createLazyRoute(
  () => import('../ProfileSettings'),
  createFallback("Profil Ayarları", "table")
);

export const LazyCompanySettings = createLazyRoute(
  () => import('../CompanySettings'),
  createFallback("Şirket Ayarları", "table")
);

export const LazyUserPermissions = createLazyRoute(
  () => import('../UserPermissions'),
  createFallback("Kullanıcı İzinleri", "table")
);

export const LazySystemSettings = createLazyRoute(
  () => import('../SystemSettings'),
  createFallback("Sistem Ayarları", "table")
);

// Communication & Support
export const LazyPhoneRecords = createLazyRoute(
  () => import('../PhoneRecords'),
  createFallback("Telefon Kayıtları", "table")
);

export const LazyComplaintRegister = createLazyRoute(
  () => import('../ComplaintRegister'),
  createFallback("Şikayet Kayıtları", "table")
);

// Expense Management — 13.09.2026: Masraf Ekranı Kasa sayfasına taşındı (src/pages/Kasa.tsx).

// ExpenseReports silindi - ExpenseScreen kullanılıyor

// Authentication
export const LazySignIn = createLazyRoute(
  () => import('../SignIn'),
  createFallback("Giriş Sayfası", "table")
);

export const LazyLandingPage = createLazyRoute(
  () => import('../LandingPage'),
  createFallback("Ana Sayfa", "table")
);

export const LazyInitialRegister = createLazyRoute(
  () => import('../InitialRegister'),
  createFallback("Kayıt Formu", "table")
);

// RoleRegister silindi - kullanılmıyordu

export const LazyRoleSignIn = createLazyRoute(
  () => import('../RoleSignIn'),
  createFallback("Rol Giriş Sayfası", "table")
);

// EmailVerificationPage silindi - kullanılmıyordu

// Coming Soon component for future features
interface ComingSoonProps {
  title: string;
}

const ComingSoon = lazy(() => Promise.resolve({
  default: ({ title }: ComingSoonProps) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      textAlign: 'center',
      padding: '2rem'
    }}>
      <div>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚧</div>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#374151' }}>
          {title}
        </h2>
        <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
          Bu sayfa yakında gelecek!
        </p>
        <button
          onClick={() => window.history.back()}
          style={{
            background: '#0ea5e9',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500'
          }}
        >
          Geri Dön
        </button>
      </div>
    </div>
  )
}));

export const LazyComingSoon: React.FC<ComingSoonProps> = ({ title }) => (
  <Suspense fallback={<LoadingFallback componentName="Coming Soon" />}>
    <ComingSoon title={title} />
  </Suspense>
);

// Route bundles for better organization
export const AuthenticationRoutes = {
  SignIn: LazySignIn,
  LandingPage: LazyLandingPage,
  InitialRegister: LazyInitialRegister,
  RoleSignIn: LazyRoleSignIn,
};

export const MainRoutes = {
  SolMenu: LazySolMenu,
  DashboardContent: LazyDashboardContent,
  Wizard: LazyWizard,
};

export const CustomerRoutes = {
  CustomerList: LazyCustomerList,
  NewCustomer: LazyNewCustomer,
  CustomerExcelImporter: LazyCustomerExcelImporter,
  TransactionsList: LazyTransactionsList,
};

export const ServiceRoutes = {
  NewService: LazyNewService,
  PendingServices: LazyPendingServices,
  ReadyForDelivery: LazyReadyForDelivery,
  DeliveredServices: LazyDeliveredServices,
  // ServiceForm artık src/components/ServiceForm/ServiceForm.tsx'de - Route'larda direkt import ediliyor
};

export const StockRoutes = {
  NewStock: LazyNewStock,
  StockList: LazyStockList,
  TransferSlip: LazyTransferSlip,
  CreateWarehouse: LazyCreateWarehouse,
  StockPurchaseSlip: LazyStockPurchaseSlip,
  StockManagerWithImport: LazyStockManagerWithImport,
};

export const SettingsRoutes = {
  ProfileSettings: LazyProfileSettings,
  CompanySettings: LazyCompanySettings,
  UserPermissions: LazyUserPermissions,
  SystemSettings: LazySystemSettings,
};

export const FinancialRoutes = {
  DebtStatement: LazyDebtStatement,
  ReceivableStatement: LazyReceivableStatement,
  SalesInvoice: LazySalesInvoice,
  PurchaseInvoice: LazyPurchaseInvoice,
  SMSSender: LazySMSSender,
};

export const PersonnelRoutes = {
  NewPersonel: LazyNewPersonel,
  PersonelListesi: LazyPersonelListesi,
};

export const CommunicationRoutes = {
  PhoneRecords: LazyPhoneRecords,
  ComplaintRegister: LazyComplaintRegister,
};

// Dynamic import helper
export const dynamicImport = (path: string) => {
  return import(path).catch(_error => {
    // Return a fallback component
    return {
      default: () => (
        <div className="error-boundary">
          <h3>Module Load Error</h3>
          <p>Failed to load component. Please refresh the page.</p>
        </div>
      )
    };
  });
};

// Preload helper for critical components
export const preloadComponent = (importFunction: () => Promise<{ default: ComponentType<any> }>) => {
  return () => {
    const componentPromise = importFunction();
    componentPromise.then((_module) => {
      // Component is now loaded and cached
    }).catch((_error: unknown) => {
    });
    return componentPromise;
  };
};

// Bundle analyzer helper
export const analyzeBundle = () => {
};

// Performance monitoring
export const measureComponentLoad = (componentName: string) => {
  const startTime = performance.now();
  
  return () => {
    const endTime = performance.now();
    const loadTime = endTime - startTime;
    
    
    // Send to analytics if available
    if ((window as any).gtag) {
      (window as any).gtag('event', 'component_load_time', {
        component_name: componentName,
        load_time: loadTime
      });
    }
  };
};

// Memory usage monitoring
export const monitorMemoryUsage = () => {
  const perf = performance as any;
  if (perf.memory) {
    const memory = perf.memory;
    return {
      used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
      total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
      limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`
    };
  }
  return null;
};

// Intersection Observer for lazy loading images
export const useIntersectionObserver = (ref: React.RefObject<HTMLElement | null>, options: IntersectionObserverInit = {}) => {
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          setIsIntersecting(entry.isIntersecting);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options
      }
    );
    
    if (ref.current) {
      observer.observe(ref.current);
    }
    
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref, options]);
  
  return isIntersecting;
};

// Lazy image component
interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  placeholder?: React.ReactNode;
  className?: string;
}

export const LazyImage: React.FC<LazyImageProps> = ({ src, alt, placeholder, className, ...props }) => {
  const imgRef = React.useRef<HTMLDivElement>(null);
  const isIntersecting = useIntersectionObserver(imgRef);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);
  
  React.useEffect(() => {
    if (isIntersecting && !imageLoaded && !imageError) {
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [isIntersecting, src, imageLoaded, imageError]);
  
  return (
    <div ref={imgRef} className={`lazy-image-container ${className || ''}`}>
      {!isIntersecting && placeholder && (
        <div className="lazy-image-placeholder">
          {placeholder}
        </div>
      )}
      {isIntersecting && imageLoaded && (
        <img src={src} alt={alt} {...props} />
      )}
      {isIntersecting && imageError && (
        <div className="lazy-image-error">
          <p>Failed to load image</p>
        </div>
      )}
    </div>
  );
};

// Service Worker registration for caching
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then(_registration => {
        })
        .catch(_registrationError => {
        });
    });
  }
};

// Cache management
export const cacheManager = {
  // Set cache with expiration
  set: (key: string, value: unknown, expirationMinutes = 60) => {
    const item = {
      value,
      timestamp: Date.now(),
      expiration: expirationMinutes * 60 * 1000
    };
    localStorage.setItem(key, JSON.stringify(item));
  },
  
  // Get cache with expiration check
  get: (key: string) => {
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;
      const item = JSON.parse(itemStr);
      if (!item) return null;
      
      const now = Date.now();
      if (now - item.timestamp > item.expiration) {
        localStorage.removeItem(key);
        return null;
      }
      
      return item.value;
    } catch (error) {
      return null;
    }
  },
  
  // Clear expired cache
  clearExpired: () => {
    const keys = Object.keys(localStorage);
    keys.forEach((key: string) => {
      if (key.startsWith('cache_')) {
        cacheManager.get(key); // This will remove expired items
      }
    });
  },
  
  // Clear all cache
  clearAll: () => {
    const keys = Object.keys(localStorage);
    keys.forEach((key: string) => {
      if (key.startsWith('cache_')) {
        localStorage.removeItem(key);
      }
    });
  }
};

// Performance optimization hooks
export const usePerformanceOptimization = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [loadTime, setLoadTime] = React.useState(0);
  
  React.useEffect(() => {
    const startTime = performance.now();
    
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          const endTime = performance.now();
          setLoadTime(endTime - startTime);
        }
      },
      { threshold: 0.1 }
    );
    
    return () => observer.disconnect();
  }, []);
  
  return { isVisible, loadTime };
};

// Bundle size optimization
export const optimizeBundleSize = () => {
  // Remove unused imports
  const removeUnusedImports = (code: string) => {
    // This would be handled by webpack/babel in production
    return code;
  };
  
  // Tree shaking optimization
  const optimizeTreeShaking = () => {
    // Use ES6 modules for better tree shaking
    // Avoid default exports when possible
    // Use named exports
  };
  
  // Code splitting optimization
  const optimizeCodeSplitting = () => {
    // Split vendor libraries
    // Split routes
    // Split large components
  };
  
  return {
    removeUnusedImports,
    optimizeTreeShaking,
    optimizeCodeSplitting
  };
};

export default {
  withLazyLoading,
  createLazyRoute,
  dynamicImport,
  preloadComponent,
  analyzeBundle,
  measureComponentLoad,
  monitorMemoryUsage,
  useIntersectionObserver,
  LazyImage,
  registerServiceWorker,
  cacheManager,
  usePerformanceOptimization,
  optimizeBundleSize
};
